import deepmerge from "deepmerge";
import * as _ from "lodash";
import { flow, observable } from "mobx";
import * as QueryString from "qs";
import {
  unsavedChanges,
  unsavedChangesDialog,
} from "../business-components/common/unsavedChanges";
import {
  PostMessageCVPRedirectHost,
  PostMessageTypes,
} from "../constants/PostMessageTypes";
import {
  getCurrentPathBinding,
  getPathBindings,
  replaceCvpageParameterWithPathBinding,
} from "../settings/pathBinding";
import { settings } from "../settings/settings";
import { lp } from "../utils/logPrefix";
import { removeUndefined } from "../utils/util";
import { evaluatePath, getCurrentPath } from "./getCurrentPath";
import {
  CVPSearchParams,
  keepOnlyParamsThatBelongToPath,
  minimizeQueryStringValues,
  queryStringStore,
} from "./wsQueryStringStore";
import { routerStore } from "./WSRouter";
import { UnsavedChangesStrategy } from "./MobxReactRouter";
import { getAppType, isAppTypeIframe } from "../getAppType";

export interface IGetRedirectPropsResponse {
  href?: string;
  target?: "_blank" | undefined;
  onClick?: (event: any) => any;
}

export type IGetRedirectProps = (
  targetUrl: string,
  options: IRedirectOptions
) => IGetRedirectPropsResponse;

export const getRedirectProps: IGetRedirectProps = (_targetUrl, _options) => {
  let { targetUrl, options } = computeRedirect(_targetUrl, _options);

  return {
    // include href in addition to onclick to enable browser functionality (open in new window etc)
    href: targetUrl,
    target: options.target,
    onClick: (_event) => {
      const event = _event?.originalEvent ?? _event;

      if (event) {
        if (event.persist) event.persist(); // just a precaution
        if (event.preventDefault) event.preventDefault(); // prevent "normal" redirect
      }

      if (event?.ctrlKey) {
        options = {
          ...options,
          target: "_blank",
        };
      }

      // using _targetUrl here since redirect expects that one
      redirect(_targetUrl, options);

      if (options.onClick) {
        return options.onClick(event);
      }

      // prevent form submit in old cvp
      return false;
    },
  };
};

export const redirect = flow(function* (_targetUrl: string, _options: IRedirectOptions) {
  const { targetUrl, options, pathBindingGroupKey } = computeRedirect(
    _targetUrl,
    _options
  );

  redirectCounter.set(redirectCounter.get() + 1);

  // legacy unsaved changes handling, only used on /admin and /questionnaire pages
  if (options.unsavedChangesStrategy === "check") {
    for (let i = 0; i < unsavedChanges.get().length; i++) {
      let allow = unsavedChangesDialog();

      if (allow && (allow as any).then) allow = yield allow;

      if (!allow) return RedirectResult.unsavedChanges;
    }
  }

  ///////////////// handle state
  const currentState: any = routerStore.location.state || {};
  let state: any = options.state || {};
  if (typeof options.mergeState === "object") {
    state = deepmerge(currentState, options.mergeState);
  }

  /////////////////

  if (options.target) {
    window.open(targetUrl, options.target);
    return RedirectResult.ok;
  }

  let scrollToY = options.scrollToY;
  if (scrollToY === undefined) {
    scrollToY = shouldScrollToTopWhenNavigating(targetUrl) ? 0 : undefined;
  }

  const hardRedirect = shouldHardRedirect(pathBindingGroupKey, options);

  if (window.cvpEmbedType === "iframe" && !window.iframe_preventNavigationTakeover) {
    const origin = new URL(routerStore.hostUrl).origin;

    const msg: PostMessageCVPRedirectHost = {
      type: PostMessageTypes.CVPRedirectHost,
      url: targetUrl,
      method: options.method,
      scrollToY,
      hardRedirect,
    };

    console.log(lp("window.parent.postMessage"), msg);

    // unsaved changes is not checked in iframe
    // unsaved changes is not checked in iframe
    // unsaved changes is not checked in iframe
    // unsaved changes is not checked in iframe
    // unsaved changes is not checked in iframe
    window.parent.postMessage(msg, origin);
  } else {
    routerStore.unsavedChangesStrategy = options.unsavedChangesStrategy;

    if (hardRedirect && options.method === "replace") {
      window.location.replace(targetUrl);
    } else if (hardRedirect && options.method === "push") {
      window.location.href = targetUrl;
    }

    if (!hardRedirect && options.method === "push") {
      routerStore.history.push(targetUrl, state); // unsaved changes is checked here
    } else if (!hardRedirect && options.method === "replace") {
      routerStore.history.replace(targetUrl, state); // unsaved changes is checked here
    }

    routerStore.unsavedChangesStrategy = undefined;

    if (scrollToY) {
      window.scrollTo({ behavior: "instant", top: scrollToY });
    }
  }

  return RedirectResult.ok;
});

function computeRedirect(_targetUrl: string, options: IRedirectOptions) {
  options = completeOptions(options);
  const { targetUrl, pathBindingGroupKey } = shapeTargetUrl(_targetUrl, options);

  return {
    targetUrl,
    options,
    pathBindingGroupKey,
  };
}

function completeOptions(options: IRedirectOptions): IRedirectOptions {
  return {
    method: "push",
    mergeQs: null,
    redirectRaw: false,
    unsavedChangesStrategy: UnsavedChangesStrategy.check,
    ...removeUndefined(options),
  };
}

export interface ILocationState {
  [x: string]: any;
}

export interface IRedirectOptions {
  method?: "push" | "replace";
  onClick?: (evt) => any;
  target?: "_blank";
  clearLeftView?: boolean;
  clearSplitView?: boolean;

  state?: ILocationState;

  /**
   * true:
   * merge current and targetUrl querystrings
   *
   * object:
   * overwrite current querystring with given values, a null value removes the keyvalue pair completely
   */
  mergeQs: { [key: string]: string | number | null } | null;

  mergeState?: ILocationState;
  scrollToY?: number;

  unsavedChangesStrategy?: UnsavedChangesStrategy;

  /** Does a hard redirect without modifying target url. In iframe it does
   * a hard redirect of host window, also without modifying target url.
   */
  redirectRaw?: boolean;

  hardRedirect?: boolean;
}

const redirectCounter = observable.box(0);

export enum RedirectResult {
  ok = "ok",
  unsavedChanges = "unsavedChanges",
}

//TODO form url so that hash comes after search
function shapeTargetUrl(targetUrl: string, options: IRedirectOptions) {
  if (options.redirectRaw) {
    return { targetUrl };
  }

  if (targetUrl === null) targetUrl = getCurrentPath();
  targetUrl = removeAppPrefix(targetUrl);

  let pathBindingGroupKey;

  if (window.cvpEmbedType && !window.iframe_preventNavigationTakeover) {
    const adjusted = adjustMainViewTargetUrlForEmbed(targetUrl, options);
    targetUrl = adjusted.url;
    pathBindingGroupKey = adjusted.pathBindingGroupKey;
  } else {
    targetUrl = addQueryStringToUrl(targetUrl, null, options);
  }

  if (options.target && targetUrl[0] === "/") {
    targetUrl = window.location.origin + targetUrl;
  }

  return { targetUrl, pathBindingGroupKey };
}

function shouldHardRedirect(pathBindingGroupKey: string, options: IRedirectOptions) {
  if (options.hardRedirect) return true;
  if (options.redirectRaw) return true;
  if (!isAppTypeIframe()) return false;
  if (!settings.get().iframe_pathBinding) return false;

  const currentPathBinding = getCurrentPathBinding(
    routerStore.hostUrl,
    settings.get(),
    !!window.cvpSandboxType
  );

  return pathBindingGroupKey !== currentPathBinding?.key;
}

function shouldScrollToTopWhenNavigating(targetUrl: string) {
  const currentPath = getCurrentPath();
  const newPath = evaluatePath(targetUrl);

  return currentPath !== newPath;
}

const removeAppPrefix = (url: string) => {
  if ((url || "").indexOf(window.location.origin) === 0) {
    url = url.substring(window.location.origin.length);
  }

  return url;
};

const addQueryStringToUrl = (
  targetUrl: string,
  pathForResolvingQueryParams: string | null,
  options: IRedirectOptions
) => {
  if (!options.mergeQs) return targetUrl;

  // merge current and target querystrings

  const hasProtocol = targetUrl.startsWith("http://") || targetUrl.startsWith("https://");

  if (!hasProtocol && !targetUrl.startsWith("/")) {
    targetUrl = "/" + targetUrl;
  }

  const _targetUrl = new URL(hasProtocol ? targetUrl : "tempprotocol://" + targetUrl);

  let targetUrlQs = [..._targetUrl.searchParams.entries()].reduce((acc, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});

  const currentQs = queryStringStore.qs;

  const _pathForResolvingQueryParams = pathForResolvingQueryParams || _targetUrl.pathname;

  // filter current querystring so we dont keep irrelevant querystring parameters
  const keepQs = keepOnlyParamsThatBelongToPath(_pathForResolvingQueryParams, currentQs);
  targetUrlQs = keepOnlyParamsThatBelongToPath(_pathForResolvingQueryParams, targetUrlQs);

  const qs = {
    ...keepQs,
    ...targetUrlQs,
    ...removeUndefined(options.mergeQs),
  };

  Object.entries(options.mergeQs).forEach(([key, value]) => {
    if (value === null) {
      delete qs[key];
    }
  });

  _targetUrl.search = new URLSearchParams(minimizeQueryStringValues(qs)).toString();

  return _targetUrl.toString().replace("tempprotocol://", "");
};

const adjustMainViewTargetUrlForEmbed = (
  targetUrl: string,
  options: IRedirectOptions
) => {
  let [_url, _params] = targetUrl.split("?");

  let iframe_startPage = settings.get().iframe_startPage;

  if (_url.endsWith("/")) _url = _url.slice(0, _url.length - 1);
  if (_url.startsWith("/")) _url = _url.slice(1);

  if (iframe_startPage.endsWith("/"))
    iframe_startPage = iframe_startPage.slice(0, _url.length - 1);
  if (iframe_startPage.startsWith("/")) iframe_startPage = iframe_startPage.slice(1);

  const pathBindings = getPathBindings(settings.get(), !!window.cvpSandboxType);
  const hasPathBindings = Object.keys(pathBindings).length > 0;

  const pathForResolvingQueryParams = _url;

  function getCvPage() {
    if (hasPathBindings) return _url;
    if (_url === iframe_startPage) return null; // CVPSearchParams.cvpage is redundant on the start page
    return _url;
  }

  targetUrl = addQueryStringToUrl(
    routerStore.hostUrl ?? window.location.href,
    pathForResolvingQueryParams,
    {
      ...options,
      mergeQs: {
        ...options.mergeQs,
        [CVPSearchParams.cvpage]: getCvPage() || null,
      },
    }
  );

  const urlBeforePathBinding = targetUrl;

  const { didReplace, url, cvpage, pathBindingGroupKey } =
    replaceCvpageParameterWithPathBinding(
      settings.get(),
      targetUrl,
      !!window.cvpSandboxType
    );

  return {
    url,
    urlBeforePathBinding,
    usedPathBinding: didReplace,
    cvpage,
    pathBindingGroupKey,
  };
};
